/**
 * Marquee Module
 *
 * GSAP-powered infinite scrolling marquee with scroll-based interactions.
 * Works with normalize/dupe module for content duplication.
 *
 * Usage:
 *   <div data-hs-marquee="wrapper" data-hs-marquee-direction="left">
 *     <div data-hs-dupe="child" data-hs-dupe-count="2">
 *       <!-- Content here -->
 *     </div>
 *   </div>
 *
 * Scroll Behavior:
 *   - Default: Speeds up when scrolling (magnitude only)
 *   - Type "reverse": Scroll down speeds up, scroll up reverses animation
 *   - Smooth velocity interpolation for buttery transitions
 */

import {
  globalConfig,
  getGsap,
  prefersReducedMotion,
  warnMissingDependencies,
  initScrollVelocityTracking,
  initScrollTriggerRefresh,
  getScrollVelocity,
  getScrollDirection,
  type GsapTimeline,
} from '@utils';

interface GsapInstance {
  timeline: (vars?: Record<string, unknown>) => GsapTimeline;
  set: (target: unknown, vars: Record<string, unknown>) => void;
  registerPlugin: (...plugins: unknown[]) => void;
  ticker: {
    add: (callback: () => void) => void;
    remove: (callback: () => void) => void;
  };
}

// Selectors and Attributes
const SELECTORS = {
  wrapper: '[data-hs-marquee="wrapper"]',
};

const ATTRIBUTES = {
  direction: 'data-hs-marquee-direction',
  duration: 'data-hs-marquee-duration',
  scroll: 'data-hs-marquee-scroll',
  type: 'data-hs-marquee-type',
};

const DEFAULTS = {
  duration: 25,
  scrollMultiplier: 0,
};

export async function init() {
  // Skip if user prefers reduced motion
  if (prefersReducedMotion()) {
    return { result: 'marquee skipped - prefers-reduced-motion enabled' };
  }

  const gsapLib = getGsap('marquee');
  if (!gsapLib) {
    return { result: 'marquee skipped - GSAP not loaded' };
  }

  const { gsap, ScrollTrigger } = gsapLib;

  // Warn about missing required dependencies (marquee needs ScrollTrigger for scroll effects)
  warnMissingDependencies('marquee', ['ScrollTrigger'], 'error');

  if (ScrollTrigger) {
    gsap.registerPlugin(ScrollTrigger);
  }

  // Initialize global scroll utilities (only run once)
  initScrollVelocityTracking();
  initScrollTriggerRefresh();

  const marquees = Array.from(document.querySelectorAll(SELECTORS.wrapper));
  const cleanup = {
    timelines: [] as GsapTimeline[],
    scrollTriggers: [] as any[],
    tickers: [] as Array<{ tickerFunc: () => void; gsap: GsapInstance }>,
  };

  marquees.forEach((marquee) => {
    const direction = marquee.getAttribute(ATTRIBUTES.direction);
    const children = Array.from(marquee.children);

    if (children.length === 0) {
      console.warn('[marquee] No children found in marquee wrapper:', marquee);
      return;
    }

    // Get duration - check attribute first, then use default
    const durationAttr = marquee.getAttribute(ATTRIBUTES.duration);
    const duration = durationAttr ? parseFloat(durationAttr) : DEFAULTS.duration;

    // Get scroll multiplier - check attribute first, then use default
    const scrollAttr = marquee.getAttribute(ATTRIBUTES.scroll);
    const scrollMultiplier = scrollAttr ? parseFloat(scrollAttr) : DEFAULTS.scrollMultiplier;

    // Get marquee type (reverse or default)
    const marqueeType = marquee.getAttribute(ATTRIBUTES.type);

    // Set attributes with defaults if not present (for visibility in DOM)
    if (!durationAttr) {
      marquee.setAttribute(ATTRIBUTES.duration, String(DEFAULTS.duration));
    }
    if (!scrollAttr) {
      marquee.setAttribute(ATTRIBUTES.scroll, String(DEFAULTS.scrollMultiplier));
    }

    // Add class to indicate GSAP is controlling animation
    marquee.classList.add(globalConfig.classes.gsap);

    // Create GSAP timeline
    const tl = gsap.timeline({ repeat: -1 });

    // Animate all children together at the same time
    children.forEach((child) => {
      if (direction === 'left') {
        // Left: 0% → -100%
        tl.to(
          child,
          {
            xPercent: -100,
            duration: duration,
            ease: 'none',
          },
          0
        );
      } else if (direction === 'right') {
        // Right: -100% → 0%
        gsap.set(child, { xPercent: -100 }); // Start from -100%
        tl.to(
          child,
          {
            xPercent: 0,
            duration: duration,
            ease: 'none',
          },
          0
        );
      }
    });

    cleanup.timelines.push(tl);

    // Add scroll-based speed boost if scroll multiplier is set
    if (scrollMultiplier > 0) {
      let lastTimeScale = 1;

      const tickerFunc = () => {
        // Get global scroll velocity and direction
        const scrollSpeed = getScrollVelocity();
        const scrollDir = getScrollDirection();
        let newTimeScale = 1;

        if (marqueeType === 'reverse') {
          // Reverse type: scroll down speeds up, scroll up reverses
          if (scrollDir === 1) {
            // Scrolling down: speed up in normal direction
            newTimeScale = 1 + scrollSpeed * scrollMultiplier;
          } else if (scrollDir === -1) {
            // Scrolling up: reverse the animation
            // Use absolute minimum to prevent getting stuck at 0
            const reverseScale = scrollSpeed * scrollMultiplier;
            newTimeScale = reverseScale > 0.5 ? -reverseScale : 1;
          } else {
            // Not scrolling: return to base speed
            newTimeScale = 1;
          }
        } else {
          // Default type: just speed up based on velocity (no direction)
          const speedBoost = scrollSpeed * scrollMultiplier;
          newTimeScale = 1 + speedBoost;
        }

        // Only update if changed (avoid unnecessary updates)
        if (Math.abs(newTimeScale - lastTimeScale) > 0.001) {
          tl.timeScale(newTimeScale);
          lastTimeScale = newTimeScale;
        }
      };

      // Add ticker function to GSAP's ticker
      gsap.ticker.add(tickerFunc);

      // Store ticker function and gsap reference for cleanup
      cleanup.tickers.push({ tickerFunc, gsap: gsap as GsapInstance });
    }
  });

  return {
    result: `marquee initialized (${marquees.length} instances)`,
    destroy: () => {
      // Kill all timelines
      cleanup.timelines.forEach((tl) => {
        if (tl) tl.kill();
      });

      // Kill all scroll triggers
      cleanup.scrollTriggers.forEach((st) => {
        if (st) (st as any).kill();
      });

      // Remove all ticker functions
      cleanup.tickers.forEach(({ tickerFunc, gsap: gsapInstance }) => {
        gsapInstance.ticker.remove(tickerFunc);
      });

      // Remove GSAP class from all marquees
      marquees.forEach((marquee) => {
        marquee.classList.remove(globalConfig.classes.gsap);
      });

      // Clear arrays
      cleanup.timelines.length = 0;
      cleanup.scrollTriggers.length = 0;
      cleanup.tickers.length = 0;
    },
  };
}
